import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { addCompany, addCompanyUserInvitation, generateAdminAccount, writeCompanyAudit, getFeatureGroups, getFeatureKeys, Timestamp } from "@/lib/firebase";
import { formatApiError } from "@/services/api";
import { Loader2, Building2, Mail, AlertTriangle, Key } from "lucide-react";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/use-auth";

interface FeatureGroup {
  id: string;
  name: string;
}

interface FeatureKey {
  id: string;
  name: string;
  keyValue: string;
  description: string;
  enabled: boolean;
  featureGroup?: string;
  displayOrder?: number;
}

interface AddCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AddCustomerModal({
  isOpen,
  onClose,
}: AddCustomerModalProps) {
  const [companyName, setCompanyName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [companyType, setCompanyType] = useState<string>("Customer");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPartnerConfirmation, setShowPartnerConfirmation] = useState(false);
  const [partnerConfirmText, setPartnerConfirmText] = useState("");
  const [featureGroups, setFeatureGroups] = useState<FeatureGroup[]>([]);
  const [featureKeys, setFeatureKeys] = useState<FeatureKey[]>([]);
  const [featureToggles, setFeatureToggles] = useState<Set<string>>(new Set());
  const [loadingFeatureKeys, setLoadingFeatureKeys] = useState(true);
  const [errors, setErrors] = useState<{
    companyName?: string;
    adminEmail?: string;
    companyType?: string;
  }>({});
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen) {
      loadFeatureData();
    }
  }, [isOpen]);

  const loadFeatureData = async () => {
    setLoadingFeatureKeys(true);
    try {
      const [groupsArr, keysArr] = await Promise.all([
        getFeatureGroups(),
        getFeatureKeys(),
      ]);

      setFeatureGroups(groupsArr as FeatureGroup[]);

      const keys = (keysArr as FeatureKey[])
        .map(d => {
          const name: string = d.name || '';
          return { ...d, keyValue: d.keyValue || name.trim().toLowerCase().replace(/\s+/g, '') } as FeatureKey;
        })
        .filter(k => k.enabled)
        .sort((a, b) => {
          const aO = a.displayOrder ?? Infinity;
          const bO = b.displayOrder ?? Infinity;
          if (aO !== bO) return aO - bO;
          return a.name.localeCompare(b.name);
        });
      setFeatureKeys(keys);
      setFeatureToggles(new Set());
    } catch (err: any) {
      logger.error("Error loading feature data:", err);
    } finally {
      setLoadingFeatureKeys(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: { companyName?: string; adminEmail?: string; companyType?: string } = {};

    if (!companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    if (!adminEmail.trim()) {
      newErrors.adminEmail = "Administrator email address is required";
    } else if (!validateEmail(adminEmail.trim())) {
      newErrors.adminEmail = "Please enter a valid email address";
    }

    if (!companyType) {
      newErrors.companyType = "Type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (companyType === "Partner" && !showPartnerConfirmation) {
      setShowPartnerConfirmation(true);
      return;
    }

    if (companyType === "Partner" && partnerConfirmText.toLowerCase() !== "partner") {
      return;
    }

    await saveCustomer();
  };

  const buildFeaturesArray = (): Array<{ featureGroup: string; featureKeys: string[] }> => {
    const groupMap = new Map<string, string[]>();
    const ungrouped: string[] = [];

    for (const fkId of Array.from(featureToggles)) {
      const fk = featureKeys.find(k => k.id === fkId);
      if (fk?.featureGroup) {
        const arr = groupMap.get(fk.featureGroup) || [];
        arr.push(fkId);
        groupMap.set(fk.featureGroup, arr);
      } else {
        ungrouped.push(fkId);
      }
    }

    const result = Array.from(groupMap.entries()).map(([featureGroup, fkIds]) => ({
      featureGroup,
      featureKeys: fkIds,
    }));

    if (ungrouped.length > 0) {
      result.push({ featureGroup: '', featureKeys: ungrouped });
    }

    return result;
  };

  const saveCustomer = async () => {
    setIsSubmitting(true);

    try {
      logger.log("🏢 Creating new customer:", {
        companyName: companyName.trim(),
        adminEmail: adminEmail.trim(),
        type: companyType,
      });

      const features = buildFeaturesArray();

      const companyData: Record<string, any> = {
        name: companyName.trim(),
        type: companyType,
        enabled: true,
        logoUrl: null,
        status: "newCompany",
        created: Timestamp.now(),
      };

      if (features.length > 0) {
        companyData.features = features;
      }

      const companyId = await addCompany(companyData);
      logger.log("✅ Company record created with ID:", companyId);

      const toggledFeatureNames = Array.from(featureToggles)
        .map(id => featureKeys.find(k => k.id === id)?.name ?? id)
        .join(', ');

      await writeCompanyAudit(
        companyId,
        companyName.trim(),
        'created',
        user?.email ?? 'unknown',
        user?.uid ?? '',
        [
          { field: 'name', from: '', to: companyName.trim() },
          { field: 'type', from: '', to: companyType },
          { field: 'adminEmail', from: '', to: adminEmail.trim() },
          ...(features.length > 0
            ? [{ field: 'features', from: '', to: toggledFeatureNames }]
            : []),
        ]
      );

      const invitationData = {
        companyId: companyId,
        email: adminEmail.trim(),
        role: "administrator",
        status: "Pending",
        inviteDate: null,
        error: "",
        created: Timestamp.now(),
      };

      const invitationId = await addCompanyUserInvitation(invitationData);
      logger.log("✅ Invitation record created with ID:", invitationId);

      await generateAdminAccount({ id: invitationId });
      logger.log("✅ Cloud function generate_admin_account called with ID:", invitationId);

      toast({
        title: "Customer created successfully",
        description: `${companyName.trim()} has been added and an invitation sent to ${adminEmail.trim()}.`,
      });

      resetForm();
      onClose();
    } catch (error: any) {
      logger.error("❌ Error creating customer:", error);
      toast({
        title: "Failed to create customer",
        description: formatApiError(error, "create customer"),
        variant: "destructive",
        action: (
          <ToastAction altText="Retry" onClick={saveCustomer}>
            Retry
          </ToastAction>
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCompanyName("");
    setAdminEmail("");
    setCompanyType("Customer");
    setShowPartnerConfirmation(false);
    setPartnerConfirmText("");
    setErrors({});
    setFeatureToggles(new Set());
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const handleCancelPartnerConfirmation = () => {
    setShowPartnerConfirmation(false);
    setPartnerConfirmText("");
  };

  const toggleFeature = (fkId: string, val: boolean) => {
    setFeatureToggles(prev => {
      const next = new Set(prev);
      if (val) next.add(fkId);
      else next.delete(fkId);
      return next;
    });
  };

  const ungroupedKeys = featureKeys.filter(fk => !fk.featureGroup || !featureGroups.find(g => g.id === fk.featureGroup));
  const hasAnyFeatureKeys = featureKeys.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Add New Customer/Partner
          </DialogTitle>
          <DialogDescription>
            Create a new company and send an invitation to their
            administrator.
          </DialogDescription>
        </DialogHeader>

        {showPartnerConfirmation ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-2">
                <p className="text-sm text-amber-800 font-medium">Partner Confirmation Required</p>
                <p className="text-sm text-amber-700">
                  Please confirm you want this customer to act as a partner, and be able to invite other companies to use Wellbound+
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="partnerConfirm">
                Type <span className="font-semibold">partner</span> to confirm
              </Label>
              <Input
                id="partnerConfirm"
                value={partnerConfirmText}
                onChange={(e) => setPartnerConfirmText(e.target.value)}
                placeholder="Type 'partner' to confirm"
                disabled={isSubmitting}
                data-testid="input-partner-confirm"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelPartnerConfirmation}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={saveCustomer}
                disabled={isSubmitting || partnerConfirmText.toLowerCase() !== "partner"}
                data-testid="button-confirm-partner"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Confirm & Save"
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">
                Company Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter company name"
                disabled={isSubmitting}
                className={errors.companyName ? "border-red-500" : ""}
                data-testid="input-company-name"
              />
              {errors.companyName && (
                <p className="text-sm text-red-600">{errors.companyName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyType">
                Type <span className="text-red-500">*</span>
              </Label>
              <Select value={companyType} onValueChange={setCompanyType} disabled={isSubmitting}>
                <SelectTrigger data-testid="select-company-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Customer">Customer</SelectItem>
                  <SelectItem value="Partner">Partner</SelectItem>
                </SelectContent>
              </Select>
              {errors.companyType && (
                <p className="text-sm text-red-600">{errors.companyType}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminEmail">
                Administrator Email Address{" "}
                <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Mail className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                <Input
                  id="adminEmail"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="admin@company.com"
                  disabled={isSubmitting}
                  className={`pl-10 ${errors.adminEmail ? "border-red-500" : ""}`}
                  data-testid="input-admin-email"
                />
              </div>
              {errors.adminEmail && (
                <p className="text-sm text-red-600">{errors.adminEmail}</p>
              )}
            </div>

            {/* Features — grouped by feature group */}
            {loadingFeatureKeys ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-2 border-t pt-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading features...
              </div>
            ) : hasAnyFeatureKeys && (
              <div className="space-y-3 border-t pt-4">
                <Label className="flex items-center gap-1.5 text-sm font-medium">
                  <Key className="h-4 w-4" />
                  Features
                </Label>
                <p className="text-xs text-slate-500">Enable or disable features for this company.</p>

                {featureGroups.map(group => {
                  const groupKeys = featureKeys.filter(fk => fk.featureGroup === group.id);
                  if (groupKeys.length === 0) return null;
                  return (
                    <div key={group.id} className="space-y-1">
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{group.name}</p>
                      <div className="space-y-1 pl-2 border-l-2 border-slate-100">
                        {groupKeys.map(fk => (
                          <div key={fk.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-700">{fk.name}</p>
                              {fk.description && <p className="text-xs text-slate-500 truncate">{fk.description}</p>}
                            </div>
                            <Switch
                              checked={featureToggles.has(fk.id)}
                              onCheckedChange={(val) => toggleFeature(fk.id, val)}
                              disabled={isSubmitting}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {ungroupedKeys.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Other</p>
                    <div className="space-y-1 pl-2 border-l-2 border-slate-100">
                      {ungroupedKeys.map(fk => (
                        <div key={fk.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700">{fk.name}</p>
                            {fk.description && <p className="text-xs text-slate-500 truncate">{fk.description}</p>}
                          </div>
                          <Switch
                            checked={featureToggles.has(fk.id)}
                            onCheckedChange={(val) => toggleFeature(fk.id, val)}
                            disabled={isSubmitting}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="button-save"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
